import React from 'react';
import Input from 'components/Inputs/Input/Input';
import { getLabTypes } from 'api/api';
import { useQuery } from '@tanstack/react-query';
import CheckBox from 'components/Inputs/CheckBox/CheckBox';
import { sortBy } from 'ramda';
import SelectInput from 'components/Inputs/SelectInput/SelectInput';
import { LabType } from '../../../types/types';
import Modal from '../Modal';
import Icon from '../../Icon/Icon';
import Button from '../../Button/Button';

type ModalProps = {
  onCloseModal: () => void;
  onAddLabTests: (lab: { labTestTypes: LabType[]; isAtHomeTest: boolean }) => void;
  addedLabTests: LabType[];
  isRequestingLab: boolean;
};

const labsTypesQuery = () => ({
  queryKey: ['lab-types'],
  queryFn: () => getLabTypes(),
});

function LabTests({ onCloseModal, onAddLabTests, addedLabTests, isRequestingLab }: ModalProps) {
  const [filteredLabTypes, setFilteredLabTypes] = React.useState<LabType[] | undefined>(undefined);
  const [selectedLabTypes, setSelectedLabTypes] = React.useState<LabType[]>(addedLabTests);
  const [confirmLabTests, setConfirmLabTests] = React.useState(false);
  const [filterType, setFilterType] = React.useState<'ALL' | 'COVERED' | 'UNCOVERED'>();

  const showFullLabListView =
    selectedLabTypes.length > 0 && addedLabTests.length !== selectedLabTypes.length;

  const { data, isLoading } = useQuery<LabType[]>({
    ...labsTypesQuery(),
  });

  const fetchedLabTypes = sortBy(labType => !labType.isCovered, data || []) satisfies LabType[];

  const handleLabSearch = (queryValues: {
    query: string;
    type?: 'COVERED' | 'UNCOVERED' | 'ALL';
  }) => {
    let filter = [...fetchedLabTypes];

    if (queryValues.type && queryValues.type !== 'ALL') {
      filter = fetchedLabTypes.filter(test => test.isCovered === (queryValues.type === 'COVERED'));
    }

    if (queryValues.query) {
      filter = filter.filter(test =>
        test.name.toLowerCase().includes(queryValues.query.toLowerCase()),
      );
    }

    const hasNoFilters = !(queryValues.type && queryValues.type !== 'ALL') && !queryValues.query;
    setFilteredLabTypes(hasNoFilters ? undefined : filter);
    setFilterType(queryValues.type);
  };

  const handleCheckInputChange = (labTest: LabType) => {
    setSelectedLabTypes(fndLabTypes => {
      const tests = fndLabTypes.filter(fndItem => fndItem.id !== labTest.id);
      if (tests.length < fndLabTypes.length) {
        return tests;
      }
      return [...fndLabTypes, labTest];
    });
  };

  const handleSubmitLabTest = () => {
    if (selectedLabTypes.length > 0 && !confirmLabTests) {
      setConfirmLabTests(true);
      return;
    }

    const payload = {
      labTestTypes: selectedLabTypes.filter(fndLabType => !isAnAlreadyAddedLabTest(fndLabType.id)),
      isAtHomeTest: false,
    };

    onAddLabTests(payload);
  };

  const isAnAlreadyAddedLabTest = (labTestId: string) =>
    Boolean(addedLabTests.find(fndLabTest => fndLabTest.id === labTestId));

  return (
    <Modal onClose={onCloseModal} show>
      <div
        className={`flex flex-col rounded-lg bg-white tablet:w-[650px]  space-y-4 pt-8 pb-8 px-8 ${
          selectedLabTypes.length > 0 && confirmLabTests ? '' : 'tablet:min-h-[600px]'
        }`}
      >
        <div className="flex flex-col flex-1">
          <h3 className="text-lg font-semibold inline-flex whitespace-pre mb-4">Request Lab</h3>

          {!(selectedLabTypes.length > 0 && confirmLabTests) ? (
            <>
              <div className="flex flex-row mb-3 justify-between space-x-4 items-center">
                <div className="relative flex-1">
                  <Icon
                    id="iconSearch"
                    className="absolute w-[15px] text-[#8B969D] h-[15px] my-auto top-0 bottom-0 left-4"
                  />
                  <div
                    className="!pl-[30px] w-full rounded-full border-[1.5px] border-gray-300 focus-within:border-river-blue 
                  focus-within:ring-river-blue focus-within:ring-[0.3px]"
                  >
                    <Input
                      name="search"
                      autoComplete="off"
                      placeholder="Search lab test"
                      type="search"
                      className="!text-[15px] bg-white !py-[7px] rounded-full border-none focus:border-none focus:ring-transparent focus:ring-0"
                      onChange={event => {
                        handleLabSearch({ query: event.target.value, type: filterType });
                      }}
                    />
                  </div>
                </div>

                <div className="w-[180px]">
                  <SelectInput
                    value={filterType || ''}
                    name="provider"
                    className="!text-[15px] !py-[6.8px] border-[1.5px] border-gray-300 rounded-full"
                    placeholder={{
                      value: '',
                      label: 'Filter labs',
                    }}
                    options={[
                      { value: 'ALL', label: 'All Labs' },
                      { value: 'COVERED', label: 'Covered Labs' },
                      { value: 'UNCOVERED', label: 'Not Covered Labs' },
                    ]}
                    onChange={event =>
                      handleLabSearch({
                        query: '',
                        type: event.target.value as 'ALL' | 'COVERED' | 'UNCOVERED',
                      })
                    }
                  />
                </div>
              </div>

              <div className="!-mx-8">
                {isLoading ? (
                  <span className="mx-auto text-center mt-[15px] w-28 flex justify-center items-start flex-col">
                    <Icon id="loader" className="w-10 h-10 text-river-black mx-auto" />
                    <span className="loading">Please wait</span>
                  </span>
                ) : !isLoading && (filteredLabTypes || fetchedLabTypes).length === 0 ? (
                  <span className="mx-auto text-center mt-[15px] w-full block">
                    No results found
                  </span>
                ) : (filteredLabTypes || fetchedLabTypes).length > 0 ? (
                  <ul
                    className={`w-full overflow-y-scroll space-y-2 ${
                      showFullLabListView ? 'max-h-[350px]' : 'max-h-[450px]'
                    }`}
                  >
                    {(filteredLabTypes || fetchedLabTypes).map(testType => (
                      <li
                        key={testType.id}
                        role="presentation"
                        onClick={() =>
                          isAnAlreadyAddedLabTest(testType.id)
                            ? false
                            : handleCheckInputChange(testType)
                        }
                        className={`flex group flex-row space-x-4 justify-between items-center text-river-black cursor-pointer hover:bg-[#F6FAFF] py-2 px-8 ${
                          selectedLabTypes.find(fndLabTest => fndLabTest.id === testType.id)
                            ? 'bg-[#F3F3F3]'
                            : ''
                        }`}
                      >
                        <label
                          htmlFor="wantsHomeTesting"
                          className="block pl-[30px] -indent-[30px] cursor-pointer text-[15px]"
                        >
                          <CheckBox
                            id={testType.id}
                            name={testType.id}
                            value={testType.id}
                            checked={Boolean(
                              selectedLabTypes.find(fndLabTest => fndLabTest.id === testType.id),
                            )}
                            onChange={() => {}}
                            className="align-middle cursor-pointer mr-3"
                            disabled={isAnAlreadyAddedLabTest(testType.id)}
                          />
                          <span
                            className={`align-middle ${
                              selectedLabTypes.find(fndLabTest => fndLabTest.id === testType.id)
                                ? 'font-medium'
                                : ''
                            }`}
                          >
                            {testType.name} -{' '}
                            <span
                              className={`text-xs font-bold ${
                                selectedLabTypes.find(fndLabTest => fndLabTest.id === testType.id)
                                  ? 'font-semibold'
                                  : ''
                              }`}
                            >
                              {new Intl.NumberFormat('en-US', {
                                style: 'currency',
                                currency: 'USD',
                              }).format(testType.price / 100)}
                            </span>
                          </span>
                        </label>

                        <span
                          title={
                            testType.isCovered
                              ? ''
                              : "This lab is not covered by River Health's formulary"
                          }
                          className={`text-[11px] text-river-black bg-gray-100 rounded-full py-[8px] leading-none
                          px-2 items-center flex-shrink-0 text-ellipsis overflow-hidden whitespace-nowrap text-center 
                          group-hover:border group-hover:border-gray-300 group-hover:text-gray-500 ${
                            selectedLabTypes.find(fndLabTest => fndLabTest.id === testType.id)
                              ? 'border border-gray-300 text-gray-500'
                              : ''
                          }`}
                        >
                          {testType.isCovered ? 'Covered' : 'Not Covered'}
                        </span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <span className="px-[15px] pt-[15px] pb-[22px] text-center text-river-black block w-full invisible">
                    Error loading Lab Tests
                  </span>
                )}
              </div>
            </>
          ) : (
            <>
              <p className="text-sm text-river-black text-center mb-5">
                Review the labs information below to make sure it is correct before submitting a new
                request.
              </p>

              <div className="flex flex-col mb-4 tablet:w-[80%] laptop:w-[85%] mx-auto">
                <div className="bg-white rounded-xl shadow-[0_0px_15px_0px_rgba(0,0,0,0.08)]">
                  <div className="bg-river-blue text-white px-6 rounded-t-xl py-3">
                    <h3>Lab Tests Selected</h3>
                  </div>
                  <div>
                    <ul className="divide-y-[0.5px] divide-[#D7D7D7] px-2">
                      {selectedLabTypes.map(lab => (
                        <li className="text-river-black cursor-pointer" key={lab.id}>
                          <div className="py-3 px-6 flex flex-row items-center -mx-[10px] hover:bg-[#F3F3F3] ">
                            <p
                              className={`text-[15px] ${
                                isAnAlreadyAddedLabTest(lab.id) ? '!text-[#9DA4A8]' : 'font-medium'
                              }`}
                            >
                              {lab.name}
                            </p>
                            <button
                              type="button"
                              disabled={isAnAlreadyAddedLabTest(lab.id)}
                              className={`${
                                isAnAlreadyAddedLabTest(lab.id)
                                  ? 'disabled:bg-gray-300 disabled:border-gray-300 '
                                  : lab.isCovered
                                  ? 'bg-river-blue'
                                  : 'bg-river-red'
                              } w-5 h-5 rounded-full flex items-center justify-center ml-auto shrink-0`}
                            >
                              <Icon id="checkmark" className="text-white w-2 h-2" />
                            </button>
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
                {selectedLabTypes
                  .filter(lab => !isAnAlreadyAddedLabTest(lab.id))
                  .some(lab => !lab.isCovered) && (
                  <div className="mt-4 text-left text-xs px-2 -mb-5 text-river-red">
                    {`NB: Some selected labs are not covered by River Health's formulary. Do you want to submit request?`}
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        {showFullLabListView && (
          <div
            className="!mt-8 flex flex-row items-center justify-center 
            tablet:justify-end w-full space-x-4"
          >
            {confirmLabTests && (
              <Button
                label="Cancel"
                onClick={() => setConfirmLabTests(false)}
                className="w-[120px] !rounded-full !py-[10px] bg-white border border-river-blue !text-river-blue"
              />
            )}

            <Button
              label={!confirmLabTests ? 'Confirm selection' : 'Submit Request'}
              onClick={handleSubmitLabTest}
              className="w-[220px] !rounded-full !py-[10px]"
              disabled={isRequestingLab}
              loading={isRequestingLab}
            />
          </div>
        )}
      </div>
    </Modal>
  );
}

export default LabTests;
